import { API_ENDPOINT, fetchWithAuth, parseApiResponse } from "./index";

// 400 MiB — matches eventshouse-api's MAX_VIDEO_BYTES, sized for a ~6-minute
// conference presentation recording.
export const MAX_VIDEO_BYTES = 400 * 1024 * 1024;

export const ALLOWED_VIDEO_TYPES = new Set([
  "video/mp4",
  "video/quicktime", // .mov
  "video/webm",
]);

export function validatePaperVideoFile(file: File): string | null {
  if (!ALLOWED_VIDEO_TYPES.has(file.type)) {
    return "Please choose an MP4, MOV, or WebM video.";
  }
  if (file.size > MAX_VIDEO_BYTES) {
    return "Video must be 400 MB or smaller.";
  }
  return null;
}

type ApiResult<TExtra> =
  | { ok: true; status: number; data: TExtra }
  | { ok: false; status: number; message: string };

async function postJson<TExtra>(
  path: string,
  body: unknown,
): Promise<ApiResult<TExtra>> {
  try {
    const response = await fetch(`${API_ENDPOINT}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      return {
        ok: false,
        status: response.status,
        message: errorBody.error?.message ?? "Request failed",
      };
    }
    const data = await response.json();
    return { ok: true, status: response.status, data: data.data };
  } catch (err) {
    console.error(err);
    return {
      ok: false,
      status: 0,
      message: "Network error. Please try again.",
    };
  }
}

export interface PaperVideoExistsResult {
  ok: true;
  exists: boolean;
  authorName?: string;
  paperTitle?: string;
  authorEmail?: string;
  conferenceCode?: string;
}

export async function checkPaperVideoExists(
  paperId: string,
): Promise<PaperVideoExistsResult | { ok: false; message: string }> {
  try {
    const response = await fetch(
      `${API_ENDPOINT}/paper-videos/check-exists?paperId=${encodeURIComponent(paperId)}`,
    );
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      return { ok: false, message: errorBody.error?.message ?? "Request failed" };
    }
    const data = await response.json();
    return {
      ok: true,
      exists: Boolean(data.data?.exists),
      authorName: data.data?.authorName,
      paperTitle: data.data?.paperTitle,
      authorEmail: data.data?.authorEmail,
      conferenceCode: data.data?.conferenceCode,
    };
  } catch (err) {
    console.error(err);
    return { ok: false, message: "Network error. Please try again." };
  }
}

export const requestPaperVideoUploadCode = (obj: {
  paperId: string;
  paperTitle: string;
  authorName: string;
  email: string;
  conferenceCode: string;
}) => postJson<{ sent: boolean; overwrite: boolean }>("/paper-videos/upload/request-code", obj);

export const verifyPaperVideoUploadCode = (obj: {
  paperId: string;
  email: string;
  code: string;
}) => postJson("/paper-videos/upload/verify-code", obj);

export interface PaperVideoPresignResult {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export const requestPaperVideoPresign = (obj: {
  paperId: string;
  email: string;
  authorName: string;
  conferenceCode: string;
  contentType: string;
  fileSizeBytes: number;
  fileName: string;
}) => postJson<PaperVideoPresignResult>("/paper-videos/upload/presign", obj);

export interface PaperVideoRecord {
  paperId: string;
  paperTitle: string;
  authorName: string;
  authorEmail: string;
  conferenceCode: string;
  conferenceLabel: string;
  originalFileName: string;
  contentType: string;
  fileSizeBytes: string;
  uploadedAt: string;
}

export const confirmPaperVideoUpload = (obj: {
  paperId: string;
  paperTitle: string;
  email: string;
  authorName: string;
  conferenceCode: string;
  s3Key: string;
  contentType: string;
  fileSizeBytes: number;
  originalFileName: string;
}) => postJson<{ video: PaperVideoRecord }>("/paper-videos/upload/confirm", obj);

export const requestPaperVideoUpdateCode = (obj: {
  paperId: string;
  email: string;
}) => postJson<{ sent: boolean }>("/paper-videos/update/request-code", obj);

export const updatePaperVideoDetails = (obj: {
  paperId: string;
  paperTitle: string;
  authorName: string;
  email: string;
}) => postJson<{ video: PaperVideoRecord }>("/paper-videos/update", obj);

export const requestPaperVideoLookupCode = (obj: { paperId: string; email: string }) =>
  postJson("/paper-videos/lookup/request-code", obj);

export const verifyPaperVideoLookupCode = (obj: {
  paperId: string;
  email: string;
  code: string;
}) => postJson("/paper-videos/lookup/verify-code", obj);

export interface PaperVideoLookupResult extends PaperVideoRecord {
  downloadUrl: string;
  downloadUrlExpiresAt: string;
}

export const lookupPaperVideo = (obj: { paperId: string; email: string }) =>
  postJson<PaperVideoLookupResult>("/paper-videos/lookup", obj);

/**
 * Uploads directly to S3 via a presigned PUT URL, reporting progress via XHR
 * (plain `fetch` has no upload-progress events, which matters for files up to
 * 400 MB). Mirrors putFileToPresignedUrl in api/upload.ts for small images.
 */
export function putFileToPresignedUrlWithProgress(
  uploadUrl: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded / event.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error("Upload to storage failed"));
      }
    };
    xhr.onerror = () => reject(new Error("Upload to storage failed"));
    xhr.send(file);
  });
}

// ─── Admin (requires auth + super_admin role) ────────────────────────────────

export interface AdminPaperVideoRow extends PaperVideoRecord {
  id: number;
}

export interface AdminPaperVideosResult {
  total: number;
  page: number;
  limit: number;
  videos: AdminPaperVideoRow[];
}

export async function listAdminPaperVideos(options: {
  page?: number;
  limit?: number;
  search?: string;
} = {}): Promise<AdminPaperVideosResult> {
  const params = new URLSearchParams();
  if (options.page) params.set("page", String(options.page));
  if (options.limit) params.set("limit", String(options.limit));
  if (options.search) params.set("search", options.search);
  const qs = params.toString();
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/paper-videos${qs ? `?${qs}` : ""}`,
  );
  return parseApiResponse<AdminPaperVideosResult>(response);
}

export async function getAdminPaperVideoDownloadUrl(
  id: number,
): Promise<{ downloadUrl: string; expiresAt: string }> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/paper-videos/${id}/download-url`,
  );
  return parseApiResponse<{ downloadUrl: string; expiresAt: string }>(response);
}
